import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  Clock,
  MessageCircle,
  Heart,
  UserPlus,
  Star,
  AlertTriangle,
  Loader,
  RefreshCw,
  Inbox,
  BellOff,
  Sparkles,
  Gift,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import notificationService from '../services/notificationService';
import PermissionGate from '../components/PermissionGate';
import GooglePagination from '../components/GooglePagination';
import './NotificationsPage.css';

const NotificationsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });
  const [unreadCount, setUnreadCount] = useState(0);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState(null);

  const notificationTypes = {
    new_match: { label: 'تطابق جديد', labelEn: 'New Match', icon: Star, color: '#10b981', bg: '#d1fae5' },
    new_message: { label: 'رسالة جديدة', labelEn: 'New Message', icon: MessageCircle, color: '#3b82f6', bg: '#dbeafe' },
    new_follower: { label: 'متابع جديد', labelEn: 'New Follower', icon: UserPlus, color: '#8b5cf6', bg: '#ede9fe' },
    new_comment: { label: 'تعليق جديد', labelEn: 'New Comment', icon: MessageCircle, color: '#6366f1', bg: '#e0e7ff' },
    comment_reply: { label: 'رد على تعليقك', labelEn: 'Comment Reply', icon: MessageCircle, color: '#8b5cf6', bg: '#ede9fe' },
    new_like: { label: 'إعجاب جديد', labelEn: 'New Like', icon: Heart, color: '#ef4444', bg: '#fee2e2' },
    comment_like: { label: 'إعجاب على تعليقك', labelEn: 'Comment Like', icon: Heart, color: '#ec4899', bg: '#fce7f3' },
    offer_approved: { label: 'تمت الموافقة', labelEn: 'Approved', icon: Check, color: '#22c55e', bg: '#dcfce7' },
    offer_rejected: { label: 'تم الرفض', labelEn: 'Rejected', icon: AlertTriangle, color: '#ef4444', bg: '#fee2e2' },
    subscription_expiring: { label: 'الاشتراك ينتهي', labelEn: 'Expiring', icon: Clock, color: '#f59e0b', bg: '#fef3c7' },
    subscription_expired: { label: 'انتهى الاشتراك', labelEn: 'Expired', icon: AlertCircle, color: '#dc2626', bg: '#fee2e2' },
    promotion: { label: 'عرض خاص', labelEn: 'Promo', icon: Gift, color: '#ec4899', bg: '#fce7f3' },
    update: { label: 'تحديث', labelEn: 'Update', icon: TrendingUp, color: '#06b6d4', bg: '#cffafe' },
    system: { label: 'نظام', labelEn: 'System', icon: Bell, color: '#64748b', bg: '#f1f5f9' },
    team_invite: { label: 'دعوة فريق', labelEn: 'Team Invite', icon: UserPlus, color: '#1a5f4a', bg: '#d1fae5' }
  };

  const filterTabs = [
    { id: 'all', label: 'الكل', labelEn: 'All', icon: Inbox },
    { id: 'unread', label: 'غير مقروء', labelEn: 'Unread', icon: Bell },
    { id: 'matches', label: 'التطابقات', labelEn: 'Matches', icon: Sparkles },
    { id: 'messages', label: 'الرسائل', labelEn: 'Messages', icon: MessageCircle }
  ];

  const fetchNotifications = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 15 };
      
      if (activeFilter === 'unread') {
        params.isRead = 'false';
      } else if (activeFilter === 'matches') {
        params.type = 'new_match';
      } else if (activeFilter === 'messages') {
        params.type = 'new_message';
      }

      const response = await notificationService.getAll(params);
      if (response.success) {
        setNotifications(response.data.notifications || []);
        setPagination(response.data.pagination || { current: 1, total: 1, count: 0 });
        setUnreadCount(response.data.unreadCount || 0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(t('notifications.fetchError', 'حدث خطأ في تحميل الإشعارات'));
    } finally {
      setLoading(false);
    }
  }, [activeFilter, t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notification, e) => {
    e?.stopPropagation();
    if (notification.isRead) return;

    try {
      await notificationService.markAsRead(notification._id);
      setNotifications(prev =>
        prev.map(n => n._id === notification._id ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;
    setMarkingAll(true);

    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = (notification) => {
    handleMarkAsRead(notification);
    
    // Navigate based on notification type and data
    if (notification.link) {
      navigate(notification.link);
    } else if (notification.data?.offerId) {
      navigate(`/network`);
    } else if (notification.data?.requestId) {
      navigate(`/requests/${notification.data.requestId}`);
    } else if (notification.data?.conversationId) {
      navigate(`/chat/${notification.data.conversationId}`);
    } else if (notification.type === 'new_follower') {
      navigate(`/following`);
    } else if (notification.type === 'comment_reply' || notification.type === 'new_comment' || notification.type === 'comment_like') {
      navigate('/network');
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return isRTL ? 'الآن' : 'Now';
    if (diffMins < 60) return isRTL ? `${diffMins} د` : `${diffMins}m`;
    if (diffHours < 24) return isRTL ? `${diffHours} س` : `${diffHours}h`;
    if (diffDays < 7) return isRTL ? `${diffDays} ي` : `${diffDays}d`;

    return notifDate.toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
      calendar: isRTL ? 'gregory' : undefined,
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeInfo = (type) => {
    return notificationTypes[type] || notificationTypes.system;
  };

  const groupNotificationsByDate = (notifications) => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    notifications.forEach(n => {
      const date = new Date(n.createdAt);
      date.setHours(0, 0, 0, 0);
      
      let key;
      if (date.getTime() === today.getTime()) {
        key = isRTL ? 'اليوم' : 'Today';
      } else if (date.getTime() === yesterday.getTime()) {
        key = isRTL ? 'أمس' : 'Yesterday';
      } else {
        key = date.toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
          calendar: isRTL ? 'gregory' : undefined,
          weekday: 'long',
          month: 'short',
          day: 'numeric'
        });
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    });

    return groups;
  };

  const groupedNotifications = groupNotificationsByDate(notifications);

  return (
    <PermissionGate feature="PAGE_NOTIFICATIONS" type="page">
    <div className="notifications-page">
      {/* Hero Header */}
      <div className="notifications-hero">
        <div className="hero-content">
          <div className="hero-icon">
            <Bell size={28} />
          </div>
          <div className="hero-text">
            <h1>{t('notifications.title', 'الإشعارات')}</h1>
            <p>{t('notifications.subtitle', 'ابق على اطلاع بآخر التحديثات')}</p>
          </div>
        </div>
        
        {(unreadCount > 0 || pagination.count > 0) && (
          <div className="hero-stats">
            <div className="hero-stat">
              <span className="stat-number">{pagination.count || 0}</span>
              <span className="stat-text">{t('notifications.total', 'إجمالي')}</span>
            </div>
            {unreadCount > 0 && (
              <div className="hero-stat highlight">
                <span className="stat-number">{unreadCount}</span>
                <span className="stat-text">{t('notifications.new', 'جديد')}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Bar */}
      <div className="notifications-actions">
        <div className="filter-tabs">
          {filterTabs.map(tab => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`filter-tab ${activeFilter === tab.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(tab.id)}
              >
                <TabIcon size={16} />
                <span>{isRTL ? tab.label : tab.labelEn}</span>
                {tab.id === 'unread' && unreadCount > 0 && (
                  <span className="tab-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="action-buttons">
          <button 
            className="action-btn icon-only" 
            onClick={() => fetchNotifications()}
            title={t('common.refresh', 'تحديث')}
          >
            <RefreshCw size={18} className={loading ? 'spin' : ''} />
          </button>
          
          {unreadCount > 0 && (
            <button
              className="action-btn primary"
              onClick={handleMarkAllAsRead}
              disabled={markingAll}
            >
              {markingAll ? (
                <Loader className="spin" size={16} />
              ) : (
                <CheckCheck size={16} />
              )}
              <span className="btn-text">{t('notifications.markAllRead', 'تحديد الكل كمقروء')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => fetchNotifications()}>
            {t('common.retry', 'إعادة المحاولة')}
          </button>
        </div>
      )}

      {/* Content */}
      <div className="notifications-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner">
              <Loader size={36} className="spin" />
            </div>
            <p>{t('common.loading', 'جاري التحميل...')}</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <BellOff size={56} />
            </div>
            <h3>{t('notifications.empty', 'لا توجد إشعارات')}</h3>
            <p>{t('notifications.emptyDesc', 'ستظهر إشعاراتك هنا عند تلقي تحديثات جديدة')}</p>
          </div>
        ) : (
          <div className="notifications-list">
            {Object.entries(groupedNotifications).map(([date, items]) => (
              <div key={date} className="notification-group">
                <div className="group-header">
                  <span className="group-date">{date}</span>
                  <span className="group-line"></span>
                </div>
                
                <div className="group-items">
                  {items.map((notification) => {
                    const typeInfo = getTypeInfo(notification.type);
                    const IconComponent = typeInfo.icon;

                    return (
                      <div
                        key={notification._id}
                        className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        {!notification.isRead && <div className="unread-dot" />}
                        
                        <div 
                          className="item-icon"
                          style={{ backgroundColor: typeInfo.bg, color: typeInfo.color }}
                        >
                          <IconComponent size={20} />
                        </div>
                        
                        <div className="item-content">
                          <div className="item-header">
                            <span className="item-type" style={{ color: typeInfo.color }}>
                              {isRTL ? typeInfo.label : typeInfo.labelEn}
                            </span>
                            <span className="item-time">
                              <Clock size={12} />
                              {formatDate(notification.createdAt)}
                            </span>
                          </div>
                          <h4 className="item-title">
                            {isRTL ? (notification.titleAr || notification.titleEn) : (notification.titleEn || notification.titleAr)}
                          </h4>
                          {(notification.messageAr || notification.messageEn) && (
                            <p className="item-body">
                              {isRTL ? (notification.messageAr || notification.messageEn) : (notification.messageEn || notification.messageAr)}
                            </p>
                          )}
                        </div>

                        {!notification.isRead && (
                          <button 
                            className="mark-read-btn"
                            onClick={(e) => handleMarkAsRead(notification, e)}
                            title={t('notifications.markRead', 'تحديد كمقروء')}
                          >
                            <Check size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && pagination.total > 1 && (
        <GooglePagination
          currentPage={pagination.current}
          totalPages={pagination.total}
          onPageChange={(page) => fetchNotifications(page)}
          prevLabel={t('common.previous', 'السابق')}
          nextLabel={t('common.next', 'التالي')}
          infoPrefix={t('common.page', 'صفحة')}
          infoMiddle={t('common.of', 'من')}
          isRTL={isRTL}
          className="notifications-pagination"
        />
      )}
    </div>
    </PermissionGate>
  );
};

export default NotificationsPage;
